import * as Cesium from "cesium";

// Shader sources defined inline
// In production, these could be loaded from separate .glsl files using webpack raw-loader

const updateShaderSource = `
in vec2 v_textureCoordinates;

uniform sampler2D particleStateTexture;
uniform sampler2D windFieldTexture;
uniform sampler2D randomTexture;
uniform float deltaTime;
uniform float particleLifetime;
uniform float speedFactor;
uniform vec3 windFieldBounds;
uniform vec3 windFieldOrigin;
uniform float fadeOpacity;
uniform float turbulenceFactor;
uniform vec2 particleTextureSize;
uniform vec3 baseWindVelocity;
uniform mat3 enuToEcefMatrix;
uniform mat3 ecefToEnuMatrix;

vec3 sampleWindField(vec3 position) {
  // Transform position offset from ECEF to local ENU coordinates
  vec3 ecefOffset = position - windFieldOrigin;
  vec3 localOffset = ecefToEnuMatrix * ecefOffset;

  vec3 normalized = localOffset / windFieldBounds;
  normalized = normalized * 0.5 + 0.5;
  normalized = clamp(normalized, 0.0, 1.0);
  vec2 texCoord = vec2(normalized.x, normalized.z);
  vec4 windData = texture(windFieldTexture, texCoord);
  vec3 velocity = windData.rgb * 2.0 - 1.0;
  velocity = velocity * speedFactor + baseWindVelocity;
  return velocity;
}

float random(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

vec4 respawnParticle(vec2 texCoord) {
  vec4 randomVals = texture(randomTexture, texCoord);
  vec3 localOffset = (randomVals.xyz - 0.5) * windFieldBounds;
  // Transform offset from local ENU to world ECEF coordinates
  vec3 worldOffset = enuToEcefMatrix * localOffset;
  vec3 newPosition = windFieldOrigin + worldOffset;
  return vec4(newPosition, 0.0);
}

bool isOutOfBounds(vec3 position) {
  // Transform position offset from ECEF to local ENU coordinates
  vec3 ecefOffset = position - windFieldOrigin;
  vec3 localOffset = ecefToEnuMatrix * ecefOffset;
  vec3 absLocalOffset = abs(localOffset);
  return any(greaterThan(absLocalOffset, windFieldBounds * 0.5));
}

void main() {
  vec4 particleState = texture(particleStateTexture, v_textureCoordinates);
  vec3 position = particleState.xyz;
  float age = particleState.w;

  age += deltaTime;

  if (age > particleLifetime || isOutOfBounds(position)) {
    out_FragColor = respawnParticle(v_textureCoordinates);
    return;
  }

  vec3 windVelocity = sampleWindField(position);

  float noise = random(v_textureCoordinates + vec2(age * 0.1));
  vec3 turbulence = vec3(
    random(v_textureCoordinates + vec2(0.0, noise)),
    random(v_textureCoordinates + vec2(noise, 0.0)),
    random(v_textureCoordinates + vec2(noise, noise))
  ) - 0.5;

  windVelocity += turbulence * turbulenceFactor;

  // Transform wind velocity from local ENU coordinates to world ECEF coordinates
  vec3 windVelocityEcef = enuToEcefMatrix * windVelocity;

  // Add transformed velocity to world position
  vec3 newPosition = position + windVelocityEcef * deltaTime;

  out_FragColor = vec4(newPosition, age);
}
`;

const renderVertexSource = `
in vec2 particleIndex;

uniform sampler2D particleStateTexture;
uniform sampler2D windFieldTexture;
uniform vec2 particleTextureSize;
uniform float particleSize;
uniform float particleLifetime;
uniform vec3 windFieldBounds;
uniform vec3 windFieldOrigin;
uniform vec3 baseWindVelocity;
uniform mat3 ecefToEnuMatrix;
uniform float speedFactor;
uniform float turbulenceFactor;
uniform float maxWindSpeed;

out float v_age;
out float v_normalizedAge;
out float v_speed;

float random(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

vec3 sampleWindField(vec3 position, vec2 texIndex, float age) {
  vec3 ecefOffset = position - windFieldOrigin;
  vec3 localOffset = ecefToEnuMatrix * ecefOffset;
  vec3 normalized = localOffset / windFieldBounds;
  normalized = normalized * 0.5 + 0.5;
  normalized = clamp(normalized, 0.0, 1.0);
  vec2 texCoord = vec2(normalized.x, normalized.z);
  vec4 windData = texture(windFieldTexture, texCoord);
  vec3 velocity = windData.rgb * 2.0 - 1.0;
  velocity = velocity * speedFactor + baseWindVelocity;

  // Add turbulence (matching compute shader)
  float noise = random(texIndex + vec2(age * 0.1));
  vec3 turbulence = vec3(
    random(texIndex + vec2(0.0, noise)),
    random(texIndex + vec2(noise, 0.0)),
    random(texIndex + vec2(noise, noise))
  ) - 0.5;

  velocity += turbulence * turbulenceFactor;

  return velocity;
}

void main() {
  vec2 texCoord = particleIndex / particleTextureSize;
  vec4 particleState = texture(particleStateTexture, texCoord);
  vec3 position = particleState.xyz;
  float age = particleState.w;

  gl_Position = czm_modelViewProjection * vec4(position, 1.0);

  float ageFactor = 1.0 - (age / particleLifetime);
  gl_PointSize = particleSize * ageFactor;

  // Sample wind velocity and calculate speed for coloring (including turbulence)
  vec3 windVelocity = sampleWindField(position, texCoord, age);
  v_speed = length(windVelocity);

  v_age = age;
  v_normalizedAge = age / particleLifetime;
}
`;

const renderFragmentSource = `
in float v_age;
in float v_normalizedAge;
in float v_speed;

uniform float fadeOpacity;
uniform float maxWindSpeed;

// Heat map color gradient: blue (slow) -> cyan -> green -> yellow -> red (fast)
vec3 speedToColor(float speed, float maxSpeed) {
  float t = clamp(speed / maxSpeed, 0.0, 1.0);

  vec3 color;
  if (t < 0.25) {
    // Blue to Cyan
    float local_t = t / 0.25;
    color = mix(vec3(0.0, 0.0, 1.0), vec3(0.0, 1.0, 1.0), local_t);
  } else if (t < 0.5) {
    // Cyan to Green
    float local_t = (t - 0.25) / 0.25;
    color = mix(vec3(0.0, 1.0, 1.0), vec3(0.0, 1.0, 0.0), local_t);
  } else if (t < 0.75) {
    // Green to Yellow
    float local_t = (t - 0.5) / 0.25;
    color = mix(vec3(0.0, 1.0, 0.0), vec3(1.0, 1.0, 0.0), local_t);
  } else {
    // Yellow to Red
    float local_t = (t - 0.75) / 0.25;
    color = mix(vec3(1.0, 1.0, 0.0), vec3(1.0, 0.0, 0.0), local_t);
  }

  return color;
}

void main() {
  vec2 coord = gl_PointCoord - 0.5;
  float dist = length(coord);

  if (dist > 0.5) {
    discard;
  }

  float edgeFade = 1.0 - smoothstep(0.3, 0.5, dist);
  float ageFade = 1.0 - v_normalizedAge;
  float alpha = fadeOpacity * edgeFade * ageFade * 0.8;

  vec3 color = speedToColor(v_speed, maxWindSpeed);

  out_FragColor = vec4(color, alpha);
}
`;

// Reuse ImprovedNoise and generateWindField from WindVisualization.js
const lerp = Cesium.Math.lerp;

const _p = [
  151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140,
  36, 103, 30, 69, 142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234,
  75, 0, 26, 197, 62, 94, 252, 219, 203, 117, 35, 11, 32, 57, 177, 33, 88, 237,
  149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165, 71, 134, 139, 48,
  27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230, 220, 105,
  92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73,
  209, 76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86,
  164, 100, 109, 198, 173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38,
  147, 118, 126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58, 17, 182, 189,
  28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101,
  155, 167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232,
  178, 185, 112, 104, 218, 246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12,
  191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49, 192, 214, 31,
  181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254,
  138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215,
  61, 156, 180,
];

for (let i = 0; i < 256; i++) {
  _p[256 + i] = _p[i];
}

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function grad(hash, x, y, z) {
  const h = hash & 15;
  const u = h < 8 ? x : y,
    v = h < 4 ? y : h === 12 || h === 14 ? x : z;
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

class ImprovedNoise {
  noise(x, y, z) {
    const floorX = Math.floor(x),
      floorY = Math.floor(y),
      floorZ = Math.floor(z);

    const X = floorX & 255,
      Y = floorY & 255,
      Z = floorZ & 255;

    x -= floorX;
    y -= floorY;
    z -= floorZ;

    const xMinus1 = x - 1,
      yMinus1 = y - 1,
      zMinus1 = z - 1;

    const u = fade(x),
      v = fade(y),
      w = fade(z);

    const A = _p[X] + Y,
      AA = _p[A] + Z,
      AB = _p[A + 1] + Z,
      B = _p[X + 1] + Y,
      BA = _p[B] + Z,
      BB = _p[B + 1] + Z;

    return lerp(
      lerp(
        lerp(grad(_p[AA], x, y, z), grad(_p[BA], xMinus1, y, z), u),
        lerp(grad(_p[AB], x, yMinus1, z), grad(_p[BB], xMinus1, yMinus1, z), u),
        v,
      ),
      lerp(
        lerp(
          grad(_p[AA + 1], x, y, zMinus1),
          grad(_p[BA + 1], xMinus1, y, zMinus1),
          u,
        ),
        lerp(
          grad(_p[AB + 1], x, yMinus1, zMinus1),
          grad(_p[BB + 1], xMinus1, yMinus1, zMinus1),
          u,
        ),
        v,
      ),
      w,
    );
  }
}

/**
 * Generate a 3D wind field using Perlin noise
 */
function generateWindField(params) {
  const {
    baseSpeed = 10.0,
    baseDirection = 45.0,
    turbulence = 0.3,
    gridSize = 32,
    altitudeLevels = 4,
  } = params;

  const noise = new ImprovedNoise();
  const windField = [];

  for (let z = 0; z < altitudeLevels; z++) {
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        const nx = x / gridSize;
        const ny = y / gridSize;
        const nz = z / altitudeLevels;

        const noiseScale = 3.0;
        const noiseX = noise.noise(nx * noiseScale, ny * noiseScale, nz * noiseScale + 100);
        const noiseY = noise.noise(nx * noiseScale, ny * noiseScale, nz * noiseScale + 200);
        const noiseZ = noise.noise(nx * noiseScale, ny * noiseScale, nz * noiseScale + 300);

        const dirRad = Cesium.Math.toRadians(baseDirection);
        const baseU = Math.cos(dirRad) * baseSpeed;
        const baseV = Math.sin(dirRad) * baseSpeed;

        const u = baseU + noiseX * baseSpeed * turbulence;
        const v = baseV + noiseY * baseSpeed * turbulence;
        const w = noiseZ * baseSpeed * turbulence * 0.3;

        windField.push({ x, y, z, u, v, w });
      }
    }
  }

  return {
    field: windField,
    gridSize,
    altitudeLevels,
    baseSpeed,
    baseDirection,
  };
}

/**
 * Convert wind field data to a GPU texture
 */
function createWindFieldTexture(context, windData) {
  const { field, gridSize, altitudeLevels } = windData;

  // Pack 3D wind field into 2D texture
  // Layout: Each row represents one altitude level
  const textureWidth = gridSize;
  const textureHeight = gridSize * altitudeLevels;

  // Create Float32Array for RGB channels (velocity components)
  const data = new Float32Array(textureWidth * textureHeight * 3);

  for (let i = 0; i < field.length; i++) {
    const wind = field[i];
    // Normalize velocity to [-1, 1] range for texture storage
    // Assuming max velocity ~30 m/s
    const maxVel = 30.0;
    data[i * 3 + 0] = wind.u / maxVel; // R = u component
    data[i * 3 + 1] = wind.v / maxVel; // G = v component
    data[i * 3 + 2] = wind.w / maxVel; // B = w component
  }

  return new Cesium.Texture({
    context: context,
    width: textureWidth,
    height: textureHeight,
    pixelFormat: Cesium.PixelFormat.RGB,
    pixelDatatype: Cesium.PixelDatatype.FLOAT,
    source: {
      arrayBufferView: data,
      width: textureWidth,
      height: textureHeight,
    },
    sampler: new Cesium.Sampler({
      minificationFilter: Cesium.TextureMinificationFilter.LINEAR,
      magnificationFilter: Cesium.TextureMagnificationFilter.LINEAR,
      wrapS: Cesium.TextureWrap.CLAMP_TO_EDGE,
      wrapT: Cesium.TextureWrap.CLAMP_TO_EDGE,
    }),
  });
}

/**
 * Create random texture for particle initialization
 */
function createRandomTexture(context, width, height) {
  const data = new Float32Array(width * height * 4);

  for (let i = 0; i < width * height; i++) {
    data[i * 4 + 0] = Math.random(); // R
    data[i * 4 + 1] = Math.random(); // G
    data[i * 4 + 2] = Math.random(); // B
    data[i * 4 + 3] = Math.random(); // A
  }

  return new Cesium.Texture({
    context: context,
    width: width,
    height: height,
    pixelFormat: Cesium.PixelFormat.RGBA,
    pixelDatatype: Cesium.PixelDatatype.FLOAT,
    source: {
      arrayBufferView: data,
      width: width,
      height: height,
    },
    sampler: new Cesium.Sampler({
      minificationFilter: Cesium.TextureMinificationFilter.NEAREST,
      magnificationFilter: Cesium.TextureMagnificationFilter.NEAREST,
      wrapS: Cesium.TextureWrap.CLAMP_TO_EDGE,
      wrapT: Cesium.TextureWrap.CLAMP_TO_EDGE,
    }),
  });
}

/**
 * GPU-powered wind particle system using CustomPrimitive
 * Based on Cesium blog post: https://cesium.com/blog/2019/04/29/gpu-powered-wind/
 */
class GPUWindPrimitive {
  constructor(context, viewer, params = {}) {
    this.context = context;
    this.viewer = viewer;
    this.show = true;
    this._destroyed = false;

    // Parameters
    this.particleCount = params.particleCount || 10000;
    this.baseSpeed = params.baseSpeed || 10.0;
    this.baseDirection = params.baseDirection || 45.0;
    this.turbulence = params.turbulence || 0.3;
    this.particleLifetime = params.particleLifetime || 12.0;
    this.particleSize = params.particleSize || 8.0;
    this.fadeOpacity = params.fadeOpacity || 0.8;

    // Liège center (matching legacy wind system)
    this.centerPosition = Cesium.Cartesian3.fromDegrees(5.6185, 50.6332, params.altitude || 1000);
    this.windFieldBounds = new Cesium.Cartesian3(15000, 15000, 2000); // width, depth, height in meters

    // Generate wind field
    this.windData = generateWindField({
      baseSpeed: this.baseSpeed,
      baseDirection: this.baseDirection,
      turbulence: this.turbulence,
    });

    // Calculate base wind velocity vector in ENU coordinates
    const dirRad = Cesium.Math.toRadians(this.baseDirection);
    this.baseWindVelocity = new Cesium.Cartesian3(
      Math.cos(dirRad) * this.baseSpeed,
      Math.sin(dirRad) * this.baseSpeed,
      0.0
    );

    // Create ENU-to-ECEF transformation matrix
    // This transforms local East-North-Up vectors at centerPosition to world ECEF coordinates
    const enuTransform = Cesium.Transforms.eastNorthUpToFixedFrame(this.centerPosition);

    // Extract the 3x3 rotation matrix (upper-left of 4x4 matrix)
    this.enuToEcefMatrix = new Cesium.Matrix3(
      enuTransform[0], enuTransform[4], enuTransform[8],
      enuTransform[1], enuTransform[5], enuTransform[9],
      enuTransform[2], enuTransform[6], enuTransform[10]
    );

    // Create inverse matrix (ECEF-to-ENU) - transpose of rotation matrix
    this.ecefToEnuMatrix = Cesium.Matrix3.transpose(this.enuToEcefMatrix, new Cesium.Matrix3());

    // Particle texture dimensions (square texture to store particle state)
    this.particleTextureSize = Math.ceil(Math.sqrt(this.particleCount));

    // Time tracking
    this.lastTime = Date.now();

    // Initialize resources (deferred until first update)
    this._initialized = false;
    this._framebuffers = null;
    this._particleStateTextures = null;
    this._windFieldTexture = null;
    this._randomTexture = null;
    this._computeCommand = null;
    this._renderCommand = null;
    this._currentFramebuffer = 0; // Ping-pong index
  }

  /**
   * Initialize WebGL resources
   * Called on first update() when context is available
   */
  _initialize() {
    if (this._initialized) return;

    const context = this.context;

    // Create wind field texture
    this._windFieldTexture = createWindFieldTexture(context, this.windData);

    // Create random texture for particle spawning
    this._randomTexture = createRandomTexture(
      context,
      this.particleTextureSize,
      this.particleTextureSize
    );

    // Create particle state textures (ping-pong buffers)
    this._particleStateTextures = [
      this._createParticleStateTexture(context),
      this._createParticleStateTexture(context),
    ];

    // Create framebuffers for render-to-texture
    this._framebuffers = [
      new Cesium.Framebuffer({
        context: context,
        colorTextures: [this._particleStateTextures[0]],
        destroyAttachments: false,
      }),
      new Cesium.Framebuffer({
        context: context,
        colorTextures: [this._particleStateTextures[1]],
        destroyAttachments: false,
      }),
    ];

    // Create compute shader program for particle updates
    this._createComputeCommand();

    // Create render shader program for particle visualization
    this._createRenderCommand();

    this._initialized = true;
  }

  /**
   * Create a texture to store particle state (position + age)
   */
  _createParticleStateTexture(context) {
    // Initialize with random positions
    const data = new Float32Array(
      this.particleTextureSize * this.particleTextureSize * 4
    );

    for (let i = 0; i < this.particleTextureSize * this.particleTextureSize; i++) {
      // Random position within wind field bounds (local ENU coordinates)
      const localOffset = new Cesium.Cartesian3(
        (Math.random() - 0.5) * this.windFieldBounds.x,
        (Math.random() - 0.5) * this.windFieldBounds.y,
        (Math.random() - 0.5) * this.windFieldBounds.z
      );

      // Transform offset from local ENU to world ECEF coordinates
      const worldOffset = new Cesium.Cartesian3();
      Cesium.Matrix3.multiplyByVector(this.enuToEcefMatrix, localOffset, worldOffset);

      const position = Cesium.Cartesian3.add(
        this.centerPosition,
        worldOffset,
        new Cesium.Cartesian3()
      );

      data[i * 4 + 0] = position.x; // R = x
      data[i * 4 + 1] = position.y; // G = y
      data[i * 4 + 2] = position.z; // B = z
      data[i * 4 + 3] = Math.random() * this.particleLifetime; // A = age
    }

    return new Cesium.Texture({
      context: context,
      width: this.particleTextureSize,
      height: this.particleTextureSize,
      pixelFormat: Cesium.PixelFormat.RGBA,
      pixelDatatype: Cesium.PixelDatatype.FLOAT,
      source: {
        arrayBufferView: data,
        width: this.particleTextureSize,
        height: this.particleTextureSize,
      },
      sampler: new Cesium.Sampler({
        minificationFilter: Cesium.TextureMinificationFilter.NEAREST,
        magnificationFilter: Cesium.TextureMagnificationFilter.NEAREST,
        wrapS: Cesium.TextureWrap.CLAMP_TO_EDGE,
        wrapT: Cesium.TextureWrap.CLAMP_TO_EDGE,
      }),
    });
  }

  /**
   * Create compute command for particle physics updates
   */
  _createComputeCommand() {
    // Create fullscreen quad for compute shader
    const positions = new Float32Array([
      -1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1,
    ]);

    const geometry = new Cesium.Geometry({
      attributes: {
        position: new Cesium.GeometryAttribute({
          componentDatatype: Cesium.ComponentDatatype.FLOAT,
          componentsPerAttribute: 2,
          values: positions,
        }),
      },
      indices: new Uint16Array([0, 1, 2, 3, 4, 5]),
      primitiveType: Cesium.PrimitiveType.TRIANGLES,
    });

    const vertexArray = Cesium.VertexArray.fromGeometry({
      context: this.context,
      geometry: geometry,
      attributeLocations: { position: 0 },
      bufferUsage: Cesium.BufferUsage.STATIC_DRAW,
    });

    // Fullscreen quad vertex shader
    const computeVertexShader = `
      in vec2 position;
      out vec2 v_textureCoordinates;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        v_textureCoordinates = position * 0.5 + 0.5;
      }
    `;

    // Load update fragment shader
    const computeFragmentShader = updateShaderSource;

    const shaderProgram = Cesium.ShaderProgram.fromCache({
      context: this.context,
      vertexShaderSource: computeVertexShader,
      fragmentShaderSource: computeFragmentShader,
      attributeLocations: { position: 0 },
    });

    // Create compute command for particle physics (GPGPU)
    this._computeCommand = new Cesium.ComputeCommand({
      vertexArray: vertexArray,
      shaderProgram: shaderProgram,
      uniformMap: {},
      owner: this,
      persists: true,
    });
  }

  /**
   * Create render command for particle visualization
   */
  _createRenderCommand() {
    // Create particle indices (each particle is a point)
    const particleIndices = new Float32Array(this.particleCount * 2);

    for (let i = 0; i < this.particleCount; i++) {
      const x = i % this.particleTextureSize;
      const y = Math.floor(i / this.particleTextureSize);
      particleIndices[i * 2 + 0] = x;
      particleIndices[i * 2 + 1] = y;
    }

    const geometry = new Cesium.Geometry({
      attributes: {
        particleIndex: new Cesium.GeometryAttribute({
          componentDatatype: Cesium.ComponentDatatype.FLOAT,
          componentsPerAttribute: 2,
          values: particleIndices,
        }),
      },
      primitiveType: Cesium.PrimitiveType.POINTS,
    });

    const vertexArray = Cesium.VertexArray.fromGeometry({
      context: this.context,
      geometry: geometry,
      attributeLocations: { particleIndex: 0 },
      bufferUsage: Cesium.BufferUsage.STATIC_DRAW,
    });

    // Load render shaders
    const renderVertexShader = renderVertexSource;
    const renderFragmentShader = renderFragmentSource;

    const shaderProgram = Cesium.ShaderProgram.fromCache({
      context: this.context,
      vertexShaderSource: renderVertexShader,
      fragmentShaderSource: renderFragmentShader,
      attributeLocations: { particleIndex: 0 },
    });

    // Create bounding sphere for the wind field volume
    const boundingSphere = new Cesium.BoundingSphere(
      this.centerPosition,
      Math.sqrt(
        this.windFieldBounds.x * this.windFieldBounds.x +
        this.windFieldBounds.y * this.windFieldBounds.y +
        this.windFieldBounds.z * this.windFieldBounds.z
      ) / 2
    );

    // Create render command (will be updated in update())
    this._renderCommand = new Cesium.DrawCommand({
      vertexArray: vertexArray,
      shaderProgram: shaderProgram,
      primitiveType: Cesium.PrimitiveType.POINTS,
      renderState: Cesium.RenderState.fromCache({
        depthTest: { enabled: true },
        blending: Cesium.BlendingState.ALPHA_BLEND,
      }),
      uniformMap: {},
      modelMatrix: Cesium.Matrix4.IDENTITY,
      boundingVolume: boundingSphere,
      pass: Cesium.Pass.TRANSLUCENT,
      owner: this,
    });
  }

  /**
   * Update method called every frame by Cesium
   */
  update(frameState) {
    if (!this.show || this._destroyed) {
      return;
    }

    // Initialize on first update
    if (!this._initialized) {
      this._initialize();
    }

    // Calculate delta time
    const currentTime = Date.now();
    const deltaTime = (currentTime - this.lastTime) / 1000.0;
    this.lastTime = currentTime;

    // Update compute command uniforms and execute
    this._updateComputeCommand(frameState, deltaTime);

    // Update render command uniforms and add to command list
    this._updateRenderCommand(frameState);

    // Swap ping-pong buffers
    this._currentFramebuffer = 1 - this._currentFramebuffer;
  }

  /**
   * Update and execute compute command for particle physics
   */
  _updateComputeCommand(frameState, deltaTime) {
    const readIndex = this._currentFramebuffer;
    const writeIndex = 1 - this._currentFramebuffer;

    // Update uniforms
    this._computeCommand.uniformMap = {
      particleStateTexture: () => this._particleStateTextures[readIndex],
      windFieldTexture: () => this._windFieldTexture,
      randomTexture: () => this._randomTexture,
      deltaTime: () => deltaTime,
      particleLifetime: () => this.particleLifetime,
      speedFactor: () => 1.0,
      windFieldBounds: () => this.windFieldBounds,
      windFieldOrigin: () => this.centerPosition,
      fadeOpacity: () => this.fadeOpacity,
      turbulenceFactor: () => this.turbulence * 10.0,
      particleTextureSize: () =>
        new Cesium.Cartesian2(this.particleTextureSize, this.particleTextureSize),
      baseWindVelocity: () => this.baseWindVelocity,
      enuToEcefMatrix: () => this.enuToEcefMatrix,
      ecefToEnuMatrix: () => this.ecefToEnuMatrix,
    };

    // Set output framebuffer for render-to-texture
    this._computeCommand.framebuffer = this._framebuffers[writeIndex];
    this._computeCommand.outputTexture = this._particleStateTextures[writeIndex];

    // Execute compute command
    frameState.commandList.push(this._computeCommand);
  }

  /**
   * Update and add render command to frame state
   */
  _updateRenderCommand(frameState) {
    const readIndex = 1 - this._currentFramebuffer; // Read from just-written buffer

    // Update uniforms
    this._renderCommand.uniformMap = {
      particleStateTexture: () => this._particleStateTextures[readIndex],
      windFieldTexture: () => this._windFieldTexture,
      particleTextureSize: () =>
        new Cesium.Cartesian2(this.particleTextureSize, this.particleTextureSize),
      particleSize: () => this.particleSize,
      particleLifetime: () => this.particleLifetime,
      fadeOpacity: () => this.fadeOpacity,
      windFieldBounds: () => this.windFieldBounds,
      windFieldOrigin: () => this.centerPosition,
      baseWindVelocity: () => this.baseWindVelocity,
      ecefToEnuMatrix: () => this.ecefToEnuMatrix,
      speedFactor: () => 1.0,
      turbulenceFactor: () => this.turbulence * 10.0,
      maxWindSpeed: () => Math.max(this.baseSpeed * 1.1 + this.turbulence * 15.0, 30.0), // Max expected speed for color scale
    };

    // Add to command list
    frameState.commandList.push(this._renderCommand);
  }

  /**
   * Update wind parameters
   */
  updateParameters(params) {
    if (params.baseSpeed !== undefined) this.baseSpeed = params.baseSpeed;
    if (params.baseDirection !== undefined) this.baseDirection = params.baseDirection;
    if (params.turbulence !== undefined) this.turbulence = params.turbulence;
    if (params.particleLifetime !== undefined) this.particleLifetime = params.particleLifetime;
    if (params.particleSize !== undefined) this.particleSize = params.particleSize;
    if (params.fadeOpacity !== undefined) this.fadeOpacity = params.fadeOpacity;

    // Regenerate wind field if needed
    if (
      params.baseSpeed !== undefined ||
      params.baseDirection !== undefined ||
      params.turbulence !== undefined
    ) {
      this.windData = generateWindField({
        baseSpeed: this.baseSpeed,
        baseDirection: this.baseDirection,
        turbulence: this.turbulence,
      });

      // Update wind field texture if initialized
      if (this._initialized && this._windFieldTexture) {
        this._windFieldTexture.destroy();
        this._windFieldTexture = createWindFieldTexture(this.context, this.windData);
      }

      // Update base wind velocity
      const dirRad = Cesium.Math.toRadians(this.baseDirection);
      this.baseWindVelocity = new Cesium.Cartesian3(
        Math.cos(dirRad) * this.baseSpeed,
        Math.sin(dirRad) * this.baseSpeed,
        0.0
      );
    }
  }

  /**
   * Check if primitive is destroyed
   */
  isDestroyed() {
    return this._destroyed;
  }

  /**
   * Destroy all WebGL resources
   */
  destroy() {
    if (this._destroyed) return;

    // Destroy textures
    if (this._windFieldTexture) this._windFieldTexture.destroy();
    if (this._randomTexture) this._randomTexture.destroy();
    if (this._particleStateTextures) {
      this._particleStateTextures[0].destroy();
      this._particleStateTextures[1].destroy();
    }

    // Destroy framebuffers
    if (this._framebuffers) {
      this._framebuffers[0].destroy();
      this._framebuffers[1].destroy();
    }

    // Destroy commands (vertex arrays and shader programs)
    if (this._computeCommand) {
      this._computeCommand.vertexArray?.destroy();
      this._computeCommand.shaderProgram?.destroy();
    }
    if (this._renderCommand) {
      this._renderCommand.vertexArray?.destroy();
      this._renderCommand.shaderProgram?.destroy();
    }

    this._destroyed = true;
    return Cesium.destroyObject(this);
  }
}

/**
 * Initialise GPU-powered wind visualisation
 */
export function initialiseGPUWind(viewer, params = {}) {
  const context = viewer.scene.context;

  const gpuWind = new GPUWindPrimitive(context, viewer, {
    particleCount: params.particleCount || 10000,
    baseSpeed: params.baseSpeed || 10.0,
    baseDirection: params.baseDirection || 45.0,
    turbulence: params.turbulence || 0.3,
    altitude: params.altitude || 1000,
    particleLifetime: params.particleLifetime || 12.0,
    particleSize: params.particleSize || 8.0,
    fadeOpacity: params.fadeOpacity || 0.8,
  });

  // Add to scene primitives
  viewer.scene.primitives.add(gpuWind);

  return {
    primitive: gpuWind,
    centerPoint: gpuWind.centerPosition,
    dimensions: {
      width: gpuWind.windFieldBounds.x,
      depth: gpuWind.windFieldBounds.y,
      height: gpuWind.windFieldBounds.z,
    },
  };
}

/**
 * Fly camera to view GPU wind
 */
export function flyToGPUWindView(viewer, centerPoint) {
  viewer.camera.flyTo({
    destination: centerPoint,
    orientation: {
      heading: Cesium.Math.toRadians(0),
      pitch: Cesium.Math.toRadians(-25),
      roll: 0,
    },
    duration: 2,
  });
}
